import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import OfflineMerchantEditModal from './OfflineMerchantEditModal';
import TableToggle from './TableToggle';
import {
  buildOfflineMerchantPageParams,
  normalizeEnabledLanguages,
  resolveImageUrl,
} from './offlineMerchantConstants';

const EMPTY_FILTERS = {
  title: '',
};

const TABLE_COLUMNS = [
  'ID',
  '圖示（英語）',
  '商戶接口',
  '前台顯示（英語）',
  '是否可充值',
  '是否可提款',
  '充值順序',
  '操作',
];

export default function OfflineMerchantPage() {
  const [languages, setLanguages] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [editDetail, setEditDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingKey, setTogglingKey] = useState(null);

  async function loadLanguages() {
    try {
      const data = await adminApi.listEnabledLanguages();
      setLanguages(normalizeEnabledLanguages(Array.isArray(data) ? data : []));
    } catch (err) {
      setError(err.message || '載入語系失敗');
      setLanguages(normalizeEnabledLanguages([]));
    }
  }

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.staticIntroPage(buildOfflineMerchantPageParams(pageNo, nextFilters));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadLanguages();
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    setMessage('');
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    setMessage('');
    loadRecords(1, reset);
  }

  function openCreate() {
    setEditRow(null);
    setEditDetail(null);
    setEditOpen(true);
    setMessage('');
  }

  async function openEdit(row) {
    setEditRow(row);
    setEditDetail(null);
    setEditOpen(true);
    setMessage('');
    setDetailLoading(true);
    setError('');
    try {
      const detail = await adminApi.staticIntroDetail(row.id);
      setEditDetail(detail);
    } catch (err) {
      setError(err.message || '載入詳情失敗');
      setEditOpen(false);
      setEditRow(null);
    } finally {
      setDetailLoading(false);
    }
  }

  async function handleSave(payload) {
    setSubmitting(true);
    setError('');
    try {
      if (payload.id) {
        await adminApi.updateStaticIntro(payload);
        setMessage('已更新');
      } else {
        await adminApi.createStaticIntro(payload);
        setMessage('已新增');
      }
      setEditOpen(false);
      setEditRow(null);
      setEditDetail(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '保存失敗');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggle(row, type, nextValue, label) {
    if (!row?.id) {
      return;
    }
    const toggleKey = `${row.id}-${type}`;
    setTogglingKey(toggleKey);
    setError('');
    try {
      const updated = await adminApi.toggleStaticIntroEnabled({
        id: row.id,
        type,
        isEnabled: nextValue,
      });
      const field = type === 1 ? 'isRecharge' : 'isWithdrawal';
      setRecords((prev) =>
        prev.map((item) =>
          item.id === row.id ? { ...item, [field]: updated?.[field] ?? nextValue } : item
        )
      );
    } catch (err) {
      setError(err.message || `更新${label}失敗`);
    } finally {
      setTogglingKey(null);
    }
  }

  async function handleConfirmDelete() {
    if (!deleteTarget?.id) {
      return;
    }
    setDeleting(true);
    setError('');
    try {
      await adminApi.removeStaticIntro(deleteTarget.id);
      setDeleteTarget(null);
      setMessage('已刪除');
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '刪除失敗');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>線下商戶</h2>
        <button type="button" className="btn btn-primary" onClick={openCreate}>
          新增商戶
        </button>
      </div>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="omTitle">商戶接口 / 前台顯示</label>
          <input
            id="omTitle"
            type="text"
            value={filters.title}
            onChange={(e) => updateFilter('title', e.target.value)}
            placeholder="模糊搜尋"
          />
        </div>
        <div className="filter-actions">
          <button type="submit" className="btn btn-primary">
            搜索
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
        </div>
      </form>

      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}
      {message && <div className="alert alert-success">{message}</div>}

      {!loading && <p className="page-desc">共 {total} 筆</p>}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="empty-cell">
                  尚無紀錄
                </td>
              </tr>
            ) : (
              records.map((row) => {
                const imageUrl = resolveImageUrl(row.displayImage);
                return (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    <td className="offline-merchant-table-icon">
                      {imageUrl ? (
                        <img src={imageUrl} alt={row.displayName || 'icon'} />
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="mono-cell">{row.merchantInterface || '—'}</td>
                    <td>{row.displayName || '—'}</td>
                    <td className="table-toggle-cell">
                      <TableToggle
                        checked={row.isRecharge}
                        disabled={togglingKey === `${row.id}-1`}
                        ariaLabel={`${row.displayName} 可充值`}
                        onChange={(v) => handleToggle(row, 1, v, '可充值')}
                      />
                    </td>
                    <td className="table-toggle-cell">
                      <TableToggle
                        checked={row.isWithdrawal}
                        disabled={togglingKey === `${row.id}-2`}
                        ariaLabel={`${row.displayName} 可提現`}
                        onChange={(v) => handleToggle(row, 2, v, '可提現')}
                      />
                    </td>
                    <td>{row.sort ?? 0}</td>
                    <td>
                      <div className="table-actions">
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => openEdit(row)}
                          disabled={detailLoading && editRow?.id === row.id}
                        >
                          修改
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={() => setDeleteTarget(row)}
                        >
                          刪除
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(p) => loadRecords(p, filters)} />

      <OfflineMerchantEditModal
        open={editOpen && !detailLoading}
        row={editRow}
        detail={editDetail}
        languages={languages}
        submitting={submitting}
        onClose={() => {
          if (!submitting) {
            setEditOpen(false);
            setEditRow(null);
            setEditDetail(null);
          }
        }}
        onSubmit={handleSave}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="刪除線下商戶"
        message={
          deleteTarget
            ? `確定刪除「${deleteTarget.displayName || deleteTarget.merchantInterface || deleteTarget.id}」？`
            : ''
        }
        confirmLabel={deleting ? '刪除中…' : '確認刪除'}
        confirming={deleting}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
